// ──────────────────────────────────────────────────────
// useHeartbeat — Kiosk heartbeat hook
//
// Sends periodic heartbeat to backend for provisioned
// devices only. If backend returns 401, clears runtime
// credentials and calls onUnauthorized callback.
//
// Phase 2: Fire-and-forget, 30s interval.
// ──────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';
import { resolveIdentity, clearRuntimeCredentials, buildKioskHeaders } from '@/lib/kiosk-identity';

const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds

interface UseHeartbeatOptions {
    /** Called when heartbeat returns 401 — device should show gate */
    onUnauthorized?: () => void;
    /** Whether heartbeat should be active */
    enabled?: boolean;
}

export function useHeartbeat({ onUnauthorized, enabled = true }: UseHeartbeatOptions = {}) {
    const onUnauthorizedRef = useRef(onUnauthorized);
    useEffect(() => { onUnauthorizedRef.current = onUnauthorized; });

    useEffect(() => {
        if (!enabled) return;

        const identity = resolveIdentity();
        if (!identity.provisioned) return;

        let active = true;

        async function sendHeartbeat() {
            if (!active) return;
            const id = resolveIdentity(); // re-resolve in case credentials changed
            if (!id.provisioned) return;

            try {
                const res = await fetch('/api/backend/kiosk/heartbeat', {
                    method: 'POST',
                    headers: buildKioskHeaders(id),
                    body: JSON.stringify({ kioskId: id.kioskId }),
                });

                if (res.status === 401 && id.source === 'localStorage') {
                    // Runtime credentials rejected — clear them
                    clearRuntimeCredentials();
                    onUnauthorizedRef.current?.();
                }
            } catch {
                // Network error — fire-and-forget, don't clear credentials
            }
        }

        // Send first heartbeat immediately
        sendHeartbeat();

        const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [enabled]);
}
